import { ActionIcon, CloseButton, Group, TextInput } from "@mantine/core";
import { ArrowRight, Search } from "lucide-react";
import { useState } from "react";

import classes from "./SubmitSearch.module.css";

export interface SubmitSearchProps {
  /** The placeholder, also the box's name for a screen reader. */
  label: string;
  /** The search applied now. The box shows it again when it changes elsewhere (a reset). */
  value: string;
  /** Applies the trimmed text, on Enter or the arrow; "" when the x clears it. */
  onSearch: (value: string) => void;
  /** The most characters the box takes: the list route's limit. */
  maxLength?: number;
  /** In place of the 350px width, a page's own (the Sounds page's 316px). */
  className?: string | undefined;
}

/**
 * Live's search box over a gallery (`search-toolbar`, design-system.md 3.5): 350px, the search
 * icon, an x ("Clear input") once there is text, which clears the search at once, and a square
 * arrow ("Search"). The search applies on Enter or the arrow, never as one types.
 */
export function SubmitSearch({ label, value, onSearch, maxLength, className }: SubmitSearchProps) {
  const [typed, setTyped] = useState(value);
  const [seen, setSeen] = useState(value);
  if (value !== seen) {
    setSeen(value);
    setTyped(value);
  }
  const apply = () => onSearch(typed.trim());
  return (
    <TextInput
      className={className ?? classes.search}
      aria-label={label}
      placeholder={label}
      leftSection={<Search size={16} aria-hidden />}
      value={typed}
      {...(maxLength === undefined ? {} : { maxLength })}
      onChange={(event) => setTyped(event.currentTarget.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") apply();
      }}
      rightSectionWidth={typed === "" ? 36 : 64}
      rightSection={
        <Group gap={2} wrap="nowrap">
          {typed === "" ? null : (
            <CloseButton
              size="sm"
              aria-label="Clear input"
              onClick={() => {
                setTyped("");
                onSearch("");
              }}
            />
          )}
          <ActionIcon
            variant="subtle"
            size={28}
            aria-label="Search"
            disabled={typed.trim() === "" && value === ""}
            onClick={apply}
          >
            <ArrowRight size={16} />
          </ActionIcon>
        </Group>
      }
    />
  );
}
